/**
 * The Dashboard's chrome: the sticky top bar, the KPI strip, the tab bar,
 * the filter rail, the chip row and the tab body grid.
 *
 * Mobile-first throughout. Three rules from RESPONSIVE_SAFARI_AUDIT.md are load-
 * bearing here and are not re-litigated:
 *
 *   · every grid track is `minmax(0, 1fr)`, never a bare `1fr` — a bare track
 *     has a min-content floor and one unbreakable label bleeds the whole page
 *     sideways. Both runtime overflow defects in that audit were exactly this.
 *   · anything wider than the viewport scrolls inside ITS OWN container. The
 *     page body never scrolls sideways.
 *   · a control is >= 44px under a coarse pointer, grown with padding.
 *
 * And one that is specific to this screen: the sticky top bar keeps
 * `layout.bellDockReserve` clear on its right, because the NotificationDock is
 * `position: fixed` and will otherwise land on the Export button.
 * `src/app-surfaces.test.ts` does that arithmetic and fails if it drifts.
 */
import styled from '@emotion/styled';
import {
  duration,
  easing,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  status,
  transition,
  typography,
  zIndex,
} from '../../../../style-utils/theme';
import { down, shortLandscape, touch, up } from '../../../../style-utils/media-queries';
import { ActiveIndicator, m } from '../../../../motion';

export const Screen = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  width: 100%;
  min-width: 0;
  padding: 0 ${layout.gutterMobile} ${spacing.xl};
  overflow-wrap: anywhere;

  ${up('md')} {
    padding: 0 ${layout.gutterTablet} ${spacing.xxl};
  }
  ${up('lg')} {
    padding: 0 ${layout.gutter} ${spacing.xxl};
  }
`;

/* ────────────────────────────────  top bar  ──────────────────────────── */

export const TopBar = styled.header`
  /* NOT STICKY (ruling R-7 / decision D5, 2026-08-20). The owner asked for a
     plain bar that scrolls away with the page, and chose that over a pinned
     compact control having been shown the cost: on a long dashboard the range
     presets are now scroll-to-top-only.

     This reverses two written briefs (DASHBOARD_PROMPT.md:134 and
     CONSOLIDATION_PLAN.md:389-393 both specify a sticky bar), so it is a
     deliberate spec reversal rather than a drift.

     NO BACKTICKS IN THIS COMMENT: it is inside a styled template literal.

     position:sticky ALSO CREATES A STACKING CONTEXT, which is why this bar
     could paint over an open overlay however large that overlay's z-index was.
     That is not what fixed the profile popover — WP-01 portals it, and it is
     fixed sticky or not — but it is why the bar is no longer competing. */
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm} 0;
  background: ${palette.surfaceGlass};
  -webkit-backdrop-filter: blur(20px);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid ${palette.line};

  /* THE DOCK CLEARANCE. The bell is position:fixed and is not in this header's
     flow, so without this the Export button ends up underneath it. */
  padding-right: ${layout.bellDockReserve};

  ${shortLandscape} {
    padding-top: ${spacing.xs};
    padding-bottom: ${spacing.xs};
  }
`;

export const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  flex-wrap: wrap;
  min-width: 0;
`;

// `Title` (an h1) used to live here and was rendered by the TopBar as the word
// "Dashboard". It was a SECOND h1 on a screen whose shell already renders one,
// so the page announced its own name twice and carried two level-one headings.
// Removed with WP-11. If a bar-level heading is ever wanted again it must not be
// an h1 — the shell owns that, and useDocumentTitle is wired to it.

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  flex-wrap: wrap;
  min-width: 0;
`;

/** The scope breadcrumb. Each segment clears everything below it. */
export const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  flex-wrap: wrap;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

export const CrumbButton = styled.button<{ $current?: boolean }>`
  border: none;
  background: none;
  padding: ${spacing.half} 0;
  font: inherit;
  font-weight: ${({ $current }) =>
    $current ? typography.weight.semibold : typography.weight.regular};
  color: ${({ $current }) => ($current ? palette.ink : palette.lagoon)};
  cursor: ${({ $current }) => ($current ? 'default' : 'pointer')};

  &:focus-visible {
    outline: 2px solid ${palette.iris};
    outline-offset: 2px;
    border-radius: ${radius.xs};
  }

  /* A REAL TARGET ON A REAL FINGER. Measured at 27px in both Chromium and
     WebKit, which is under the 44px this product holds itself to — and this is
     not decoration: pressing a crumb clears the scope back to the whole
     organisation. The hit area grows only on a coarse pointer, so the density of
     the breadcrumb on a desk is untouched. */
  ${touch} {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
  }
`;

export const CrumbSep = styled.span`
  color: ${palette.slateLight};
`;

/** The range presets. A horizontal snap-scroll strip on a phone. */
export const ChipRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  flex-wrap: wrap;
  min-width: 0;

  /* WP-34 — ONE BREAKPOINT FOR THE WHOLE HEADER, and it is md.
     This switched to scroll at down(sm) while KpiStrip switched at up(md), so
     between 480 and 768 the chips wrapped while the strip beside them was still
     a carousel — three responsive contracts in one header. md is the one every
     other surface already uses: it is where the tables reflow to cards and where
     the KPI strip becomes a grid. NO BACKTICKS IN THIS COMMENT. */
  ${down('md')} {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: ${spacing.half};

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const RangeChip = styled.button<{ active?: boolean }>`
  flex: 0 0 auto;
  min-height: ${layout.compactControlHeight};
  padding: 0 ${spacing.sm};
  white-space: nowrap;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${({ active }) => (active ? palette.irisDeep : palette.slate)};
  background: ${({ active }) => (active ? palette.irisTint : palette.surface)};
  border: 1px solid ${({ active }) => (active ? palette.iris : palette.line)};
  border-radius: ${radius.pill};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    border-color: ${palette.lagoon};
    background: ${palette.irisTintSoft};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    min-height: ${layout.touchTarget};
  }
`;

export const ToggleField = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  min-height: ${layout.touchTarget};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
  cursor: pointer;
  white-space: nowrap;
`;

export const ToggleInput = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${palette.lagoon};
  cursor: pointer;
`;

export const Caption = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;

  strong {
    color: ${palette.ink};
    font-weight: ${typography.weight.semibold};
  }
`;

/** The scope-enforcement alert. Loud on purpose — it is a statement about a
 *  security control that somebody will act on. */
export const Warning = styled.div`
  display: flex;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  background: ${palette.warningSoft};
  border: 1px solid ${palette.warning};
  border-radius: ${radius.sm};
`;

export const WarningIcon = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: ${radius.circle};
  background: ${palette.warning};
  /* INK ON AMBER, NOT WHITE. White on #F59E0B measures 2.15:1 — the worst pair
     in the product — and this badge is the security banner's only glyph. Ink on
     the same amber is 7.4:1 and the amber still reads as amber, which is the
     whole point of a warning colour. */
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.bold};
`;

export const WarningBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const WarningTitle = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.bold};
  color: ${palette.ink};
`;

export const WarningText = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.inkSoft};
`;

/* ────────────────────────────────  KPI strip  ────────────────────────── */

/**
 * A GRID on a desk, a SNAP-SCROLL CARROUSEL on a phone.
 *
 * Six tiles wrapped onto three rows of two is most of a phone viewport before
 * any content exists, and the strip is meant to be a glance.
 */
export const KpiStrip = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 68%;
  gap: ${spacing.xs};
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding-bottom: ${spacing.half};
  min-width: 0;

  &::-webkit-scrollbar {
    display: none;
  }

  ${up('sm')} {
    grid-auto-columns: 40%;
  }
  ${up('md')} {
    grid-auto-flow: row;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-auto-columns: auto;
    overflow-x: visible;
  }
  ${up('lg')} {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

/**
 * A TILE IS A BUTTON, because pressing it applies the filter it describes.
 *
 * The desk feed learned this the hard way: four numbers describing a narrowing
 * the reader then had to perform by hand, on a panel whose whole argument is
 * that the answer is already on screen. `$active` is LOUD because a narrowed
 * screen looks exactly like a quiet week.
 */
export const KpiTile = styled.button<{ $active?: boolean }>`
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
  padding: ${spacing.xs} ${spacing.sm};
  text-align: left;
  font: inherit;
  background: ${({ $active }) => ($active ? palette.irisTint : palette.surface)};
  border: 1px solid ${({ $active }) => ($active ? palette.iris : palette.line)};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    border-color: ${palette.lagoon};
    background: ${({ $active }) =>
      $active ? palette.irisTint : palette.irisTintSoft};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    min-height: 96px;
  }
`;

/**
 * TWO LINES, NOT AN ELLIPSIS.
 *
 * The ellipsis was doing its job and that was the problem: at 1280 — an
 * ordinary laptop — four of the six tiles read `PEOPLE ADMI…`, `PARCELS TAK…`,
 * `PARCELS ON T…`, `PEOPLE STILL …`. The figure was large and perfectly clear
 * and what it counted was cut off, which is the wrong half to lose.
 *
 * Wrapping to a second line costs one row of 12px text and never hides a word.
 * The clamp keeps a runaway label from pushing the figure out of the tile.
 */
export const KpiLabel = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  /* slate, not slateLight: on the SELECTED tile the ground is tealTint and
     #5D7376 lands at 4.46:1 — a hair under the 4.5 this 12px label needs. */
  color: ${palette.slate};
  line-height: ${typography.leading.snug};
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const KpiFigure = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxl}px;
  font-weight: ${typography.weight.bold};
  line-height: ${typography.leading.tight};
  font-variant-numeric: tabular-nums;
  color: ${palette.ink};
`;

/* ────────────────────────  comparison mode  ─────────────────────────── */

/* ONE TILE, TWO FIGURES, AND THE DIFFERENCE UNDER THEM. A grid rather than two
   flex children so the two figures share a baseline whatever their digit count:
   "4,182" beside "97" with different baselines reads as two unrelated numbers. */
export const SplitRow = styled.span`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.half} ${spacing.xs};
  align-items: end;
  min-width: 0;
`;

export const SplitSide = styled.span<{ $muted?: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;

  /* The comparison side is quieter, never smaller. Shrinking it would make the
     two figures look like a headline and a footnote when they are two answers to
     the same question. */
  span:last-of-type {
    color: ${({ $muted }) => ($muted ? palette.slate : palette.ink)};
  }
`;

export const SplitName = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.slateLight};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* THE DIFFERENCE SPANS BOTH COLUMNS, because it belongs to neither. It is NOT
   toned with the semantic success/alert pair: more visits is not good news and
   fewer is not bad, and this product's own palette rules reserve those tokens
   for state rather than for direction. */
export const SplitDiff = styled.span`
  grid-column: 1 / -1;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  font-variant-numeric: tabular-nums;
  color: ${palette.slate};
`;

export const KpiFoot = styled.span`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  flex-wrap: wrap;
  min-height: 18px;
`;

export const KpiSpark = styled.span`
  display: block;
  position: relative;
  width: 100%;
  height: 36px;
  min-width: 0;
`;

/** The "as of now" marker on a live figure. A dot PLUS the word, never the dot
 *  alone: colour is not a label. */
export const LiveTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
  white-space: nowrap;
`;

export const LiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: ${radius.circle};
  background: ${palette.iris};
`;

/* ──────────────────────────────  tab bar  ────────────────────────────── */

/* THE STRIP HIDES ITS SCROLLBAR, SO IT HAS TO SAY SO SOME OTHER WAY (WP-33).
 *
 * A MASK RATHER THAN WP-34's ScrollShade, and this is the one place in the app
 * where that is the right call: this strip is a pill with a 24px corner radius,
 * and an absolutely-positioned shade is a rectangle - it would paint square
 * corners outside the pill at both ends. Clipping the frame instead would clip
 * the tabs' focus rings, which are the app's only focus indicator.
 *
 * A mask applies to the element's own painting, so the border radius still
 * clips it and the fade follows the pill exactly. It fades whichever end still
 * has more behind it, and nothing at an end that does not.
 */
const fadeWidth = spacing.lg; // 32px, the same width as WP-34's ScrollShade

/* `black` IS NOT A COLOUR HERE and this file's own guard would be right to ask.
   A mask gradient is read for its ALPHA — the opaque stops mean "keep this",
   the transparent ones mean "fade this out". No pixel of it is ever painted, so
   there is no token to reach for and a palette value would be misleading. */
const fadeMask = (start?: boolean, end?: boolean): string => {
  if (!start && !end) return '';

  const from = start ? `transparent 0, black ${fadeWidth}` : 'black 0';
  const to = end
    ? `black calc(100% - ${fadeWidth}), transparent 100%`
    : 'black 100%';
  const gradient = `linear-gradient(to right, ${from}, ${to})`;

  return `-webkit-mask-image: ${gradient};
    mask-image: ${gradient};`;
};

export const TabBar = styled.div<{ $fadeStart?: boolean; $fadeEnd?: boolean }>`
  display: flex;
  gap: ${spacing.half};
  padding: ${spacing.half};
  background: ${palette.surfaceMuted};
  border-radius: ${radius.pill};
  min-width: 0;

  /* WP-33 — md, NOT sm, and it is the same reconciliation WP-34 made to
     ChipRow. This strip became scrollable below 480 while the header beside it
     changed at 768, so 480-768 was the one band where five nowrap tabs had to
     fit a box that could not scroll — they shrank below their own text instead.
     md is what every other surface on this screen uses.
     NO BACKTICKS IN THIS COMMENT - it is inside a styled template literal. */
  ${down('md')} {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    ${({ $fadeStart, $fadeEnd }) => fadeMask($fadeStart, $fadeEnd)}
  }
`;

export const Tab = styled.button<{ active?: boolean }>`
  /* THE PILL BEHIND THE LABEL IS A SEPARATE, MOVING ELEMENT (TabIndicator,
     below): position relative here is what it is positioned against, and the
     label sits above it on its own layer. The button paints no active ground of
     its own any more — the pill IS the active ground. */
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  /* WP-33 — THE MISSING OVERFLOW RULE. This is flex 1 1 auto, min-width 0,
     white-space nowrap, with nothing saying what happens when the box ends up
     narrower than the text inside it, and at 320px it did: "Overview" rendered
     in a 49px box with 74px of text, which paints straight out of the padding
     box and over the neighbouring tab. That is the "text overflows its own
     control" in the QA report, measured.
     NO BACKTICKS IN THIS COMMENT - it is inside a styled template literal. */
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: ${layout.compactControlHeight};
  padding: 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${({ active }) => (active ? palette.irisDeep : palette.slate)};
  background: transparent;
  border: none;
  border-radius: ${radius.pill};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    color: ${palette.irisDeep};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  /* AND THE OVERFLOW RULE ALONE WOULD ONLY TURN THE OVERLAP INTO AN ELLIPSIS.
     Below md the bar scrolls, so a tab has no reason to shrink at all: keeping
     its intrinsic width means five readable tabs behind a swipe instead of five
     truncated ones that fit. The ellipsis above stays as the guard for a label
     longer than the whole viewport. */
  ${down('md')} {
    flex: 0 0 auto;
  }

  ${touch} {
    min-height: ${layout.touchTarget};
  }
`;

/** The label, lifted above the pill. */
export const TabLabel = styled.span`
  position: relative;
  z-index: ${zIndex.base};
`;

/** THE ONE PILL THAT MOVES. Rendered inside the active tab only; framer's
 *  layoutId carries it from the previous tab's box to this one. */
export const TabIndicator = styled(ActiveIndicator)`
  position: absolute;
  inset: 0;
  border-radius: ${radius.pill};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
`;

/* ──────────────────────  body: rail + tab content  ───────────────────── */

/* ─────────────────────  SAVED VIEWS + RAIL DISCLOSURE (QA-07)  ───────────── */

/* The row the two disclosure controls sit on. `wrap` because on a narrow desk
   window the saved-views trigger and the rail toggle together can exceed the
   content width, and a control that overflows horizontally is one the reader
   cannot reach — the horizontal-overflow class of defect this screen has
   already been fixed for once. */
export const DisclosureRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const ViewsBar = styled.div`
  position: relative;
  display: inline-flex;
`;

export const ViewsButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.sm};
  cursor: pointer;
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.white};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: 13px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
  text-align: left;

  &:hover:not(:disabled) {
    background: ${palette.porcelain};
  }
  &:disabled {
    cursor: default;
    color: ${palette.slate};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/* NOT PORTALLED, for the reason the filter rail is not: rsuite's pickers portal
   their popups to <body>, and a portalled surface above them is how this screen
   has repeatedly ended up with a calendar underneath a scrim. This menu sits in
   normal flow above its trigger with a stacking context of its own. */
export const ViewsMenu = m.div`
  position: absolute;
  top: calc(100% + ${spacing.half});
  right: 0;
  z-index: ${zIndex.sticky};
  width: min(340px, calc(100vw - ${spacing.md} * 2));
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm};
  background: ${palette.white};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};
`;

export const ViewsSaveRow = styled.div`
  display: flex;
  gap: ${spacing.xs};
`;

export const ViewsNameInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.xs};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  font-family: ${typography.ui};
  /* 16px on a touch device or iOS zooms the whole page on focus. The product's
     own rule (R-19); it applies to every new input, including this one. */
  font-size: 16px;

  ${up('lg')} {
    font-size: 14px;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const ViewsShareLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.xs};
  font-family: ${typography.ui};
  font-size: 12px;
  color: ${palette.slate};
  cursor: pointer;
`;

export const ViewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  max-height: 260px;
  overflow-y: auto;
`;

export const ViewsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};

  > button:first-of-type {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

export const ViewsDelete = styled.button`
  flex: 0 0 auto;
  width: ${layout.touchTarget};
  height: ${layout.touchTarget};
  cursor: pointer;
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.white};
  color: ${palette.slate};
  font-size: 18px;
  line-height: 1;

  &:hover:not(:disabled) {
    color: ${status.alert.fg};
    border-color: ${status.alert.dot};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const ViewsEmpty = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: 12px;
  color: ${palette.slate};
`;

/* The advanced rail's disclosure control. Rendered at lg+ only, beside the rail
   it toggles — below lg the filters are already a bottom sheet, i.e. already
   disclosed progressively, and a second control for the same idea would be one
   too many. */
export const RailToggle = styled(ViewsButton)`
  display: none;

  ${up('lg')} {
    display: inline-flex;
  }
`;

export const Body = styled.div<{ railOpen?: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing.sm};
  min-width: 0;

  ${up('lg')} {
    /* QA-07. The advanced rail is CLOSED by default now, and a closed rail must
       give its 280px back rather than leaving a gutter — a collapsed panel that
       still reserves its column reads as a rendering fault, not as a choice. */
    grid-template-columns: ${({ railOpen }) =>
      railOpen ? '280px minmax(0, 1fr)' : 'minmax(0, 1fr)'};
    /* The column opening and closing is the rail's own disclosure motion; the
       rail itself fades and slides inside it. Engines that cannot interpolate
       a grid track fall back to the instant switch. */
    transition: grid-template-columns ${duration.base} ${easing.standard};
    gap: ${spacing.md};
    align-items: start;
  }
`;

/** A sticky rail at lg+, a portalled bottom sheet below it. NOT portalled here:
 *  it is an ordinary sticky column, and sticky needs the page's own scrollport.
 *  A motion element so it can slide in beside the panels when disclosed. */
export const Rail = m.aside`
  display: none;

  ${up('lg')} {
    display: flex;
    flex-direction: column;
    gap: ${spacing.sm};
    position: sticky;
    /* The top bar no longer sticks (WP-12), so there is nothing to sit under —
       the rail pins to the top of its own scrollport instead of to a number
       that encoded the bar's height.

       BOTH max-height lines are corrected, not just the dvh one: the vh line is
       the pre-Safari-15.4 fallback and leaving it at the old number would keep
       the bug alive in exactly the engine least likely to be tested. The
       scrollport here is DashboardContentContainer — 100dvh less the 80px
       header and its 64px of padding — not the viewport. Sizing against the
       viewport overran the scrollport by ~60px and put the last filters out of
       reach entirely. */
    top: ${spacing.sm};
    max-height: calc(100vh - 144px);
    max-height: calc(100dvh - 144px);
    overflow-y: auto;
    padding-right: ${spacing.half};
    min-width: 0;
  }
`;

export const TabPanels = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
`;

/** The panel grid every tab lays its panels out on. */
export const PanelGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing.sm};
  min-width: 0;

  ${up('lg')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${spacing.md};
  }
`;

/**
 * WHAT A CHIP DID NOT DO.
 *
 * Under the chip row, because that is where the reader was told the filter is
 * on. Some filters cannot reach some figures — a name is encrypted and is not
 * expressible in SQL, a direction is a property of an event rather than a column
 * — and a chip that narrowed nothing while saying it did is the single worst
 * thing this screen can do: both numbers look right.
 *
 * Quiet rather than an alert: it is a caveat about scope, not a failure.
 */
export const FilterCaveats = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  margin: 0;
  padding: 0 0 0 ${spacing.sm};
  list-style: disc;
  min-width: 0;

  li {
    font-family: ${typography.ui};
    font-size: ${typography.size.xxs}px;
    line-height: ${typography.leading.normal};
    color: ${palette.slate};
    overflow-wrap: anywhere;
  }
`;

/* ────────────────────────────  active chips  ─────────────────────────── */

export const ChipBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  flex-wrap: wrap;
  min-width: 0;
`;

export const FilterChipButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  min-height: ${layout.compactControlHeight};
  padding: 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.irisDeep};
  background: ${palette.irisTint};
  border: 1px solid ${palette.iris};
  border-radius: ${radius.pill};
  cursor: pointer;
  transition: ${transition.colors};

  b {
    font-weight: ${typography.weight.semibold};
  }

  &:hover {
    background: ${palette.irisTintSoft};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    min-height: ${layout.touchTarget};
    padding: 0 ${spacing.sm};
  }
`;

export const ChipX = styled.span`
  font-size: 14px;
  line-height: 1;
  opacity: 0.7;
`;

export const ClearAll = styled.button`
  border: none;
  background: none;
  padding: ${spacing.half} ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.lagoon};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${palette.iris};
    outline-offset: 2px;
    border-radius: ${radius.xs};
  }
`;

export const ResultCount = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
  font-variant-numeric: tabular-nums;
  margin-left: auto;
`;

/* ─────────────────────────────  filter rail  ─────────────────────────── */

export const RailGroup = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  min-width: 0;
`;

export const RailTitle = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.slateLight};
`;

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;

  > span {
    font-family: ${typography.ui};
    font-size: ${typography.size.xxs}px;
    font-weight: ${typography.weight.semibold};
    color: ${palette.slate};
  }
`;

/* 16px, ALWAYS, on every control a finger can focus. iOS Safari zooms the whole
   page when a focused field is under 16px and does not zoom back out. The global
   rule in index.css covers the touch case; this is belt for the rest. */
const controlBase = `
  width: 100%;
  min-width: 0;
  min-height: ${layout.fieldHeight};
  padding: 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: 16px;
  color: ${palette.ink};
  background: ${palette.surface};
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.xs};
  transition: ${transition.colors};

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

export const Select = styled.select`
  ${controlBase}
`;

/* ── BAR DENSITY SELECT (WP-13 / BUG-017) ───────────────────────────────────
   The owner's report was "the select looks out of place", and it was measurably
   so: `Select` is `controlBase`, the FORM-FIELD recipe — 48px tall, radius.xs,
   16px text — sitting in a row of 32px RangeChips with pill radius and 13px
   text. Correct component, wrong row.

   This is the same control at the bar's density: compactControlHeight, pill
   radius to match the chips beside it, xs text, and the native chevron replaced
   with a drawn one because `appearance: none` removes it.

   THE TOUCH OVERRIDE IS NOT OPTIONAL. 32px is below the 44px minimum, so under a
   coarse pointer this grows back to layout.touchTarget. And the font stays 16px
   there: index.css already forces 16px on `select` under a coarse pointer
   because iOS zooms the whole page when a focused control's text is smaller —
   do not "fix" that back down to 13px to make the phone match the desk.

   NO BACKTICKS IN THIS COMMENT: it is inside a styled template literal. */
export const BarSelect = styled.select`
  flex: 0 0 auto;
  box-sizing: border-box;
  min-height: ${layout.compactControlHeight};
  max-width: 200px;
  padding: 0 ${spacing.lg} 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.slate};
  background-color: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.pill};
  cursor: pointer;
  transition: ${transition.colors};

  appearance: none;
  -webkit-appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, ${palette.slate} 50%),
    linear-gradient(135deg, ${palette.slate} 50%, transparent 50%);
  background-position: calc(100% - 14px) 50%, calc(100% - 9px) 50%;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;

  &:hover {
    border-color: ${palette.lineStrong};
    color: ${palette.ink};
  }

  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    min-height: ${layout.touchTarget};
  }
`;

export const Input = styled.input`
  ${controlBase}
`;

export const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  min-height: ${layout.touchTarget};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.ink};
  cursor: pointer;
`;

/* ─────────────────────────  the mobile filter sheet  ─────────────────── */

/* Both are motion elements: the scrim fades, the sheet rises from the bottom
   edge, and both leave the way they came (scrimMotion / useOverlayMotion in
   dashboard-screen.tsx). Portalled to body — see ModalPortal for why an inline
   fixed overlay cannot be trusted under an animated ancestor. */
export const SheetScrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.overlay};
  background: ${palette.overlay};
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);

  ${up('lg')} {
    display: none;
  }
`;

export const Sheet = m.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${zIndex.modal};
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  max-height: 80vh;
  max-height: 80dvh;
  overflow-y: auto;
  padding: ${spacing.sm};
  /* The home indicator on a notched phone sits inside the sheet otherwise. */
  padding-bottom: calc(${spacing.xl} + var(--safe-bottom));
  background: ${palette.surface};
  border-radius: ${radius.lg} ${radius.lg} 0 0;
  box-shadow: ${shadow.xl};

  ${up('lg')} {
    display: none;
  }
`;

export const SheetHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  position: sticky;
  top: 0;
  padding-bottom: ${spacing.xs};
  background: ${palette.surface};
`;

/* ────────────────────────────────  misc  ─────────────────────────────── */

/* ────────────────────────  the shortcut list  ───────────────────────── */

/* HelpScrim / HelpCard moved into chrome/shortcut-help.tsx as motion elements. */

export const HelpTitle = styled.h2`
  margin: 0 0 ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.bold};
  color: ${palette.ink};
`;

export const HelpRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

export const HelpKey = styled.kbd`
  flex: none;
  min-width: 56px;
  padding: ${spacing.half} ${spacing.half};
  font-family: ${typography.mono};
  font-size: ${typography.size.xs}px;
  color: ${palette.ink};
  text-align: center;
  background: ${palette.surfaceMuted};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
`;

export const Button = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  min-height: ${layout.buttonHeight};
  padding: 0 ${spacing.sm};
  white-space: nowrap;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${({ $primary }) => ($primary ? palette.white : palette.lagoon)};
  background: ${({ $primary }) => ($primary ? palette.lagoon : palette.surface)};
  border: 1px solid ${({ $primary }) => ($primary ? palette.iris : palette.line)};
  border-radius: ${radius.xs};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    border-color: ${palette.lagoon};
    background: ${({ $primary }) => ($primary ? palette.irisDark : palette.irisTintSoft)};
  }
  &:disabled {
    color: ${palette.slateLight};
    background: ${palette.surfaceMuted};
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/**
 * A `Button` THAT ONLY EXISTS WHERE THE RAIL DOES NOT.
 *
 * `Rail` is `display: none` below `lg`, so below `lg` the sheet is the only way
 * to reach the filters and its button has to be there. At `lg` and up the rail
 * is on screen permanently and a second door to it is clutter — the top bar
 * carries Export and nothing else.
 */
export const PhoneOnlyButton = styled(Button)`
  ${up('lg')} {
    display: none;
  }
`;

/* A popover: grows from its top-right anchor and shrinks back (overlayMotion
   'popover' at the site, transform-origin set there). Never pointer-events:none
   on the way in — the e2e suite opens these and clicks an item with no wait. */
export const Menu = m.div`
  position: absolute;
  top: calc(100% + ${spacing.half});
  right: 0;
  z-index: ${zIndex.dropdown};
  display: flex;
  flex-direction: column;
  min-width: 200px;
  padding: ${spacing.half};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  box-shadow: ${shadow.lg};
`;

/** A heading INSIDE the menu. The menu now holds two unrelated sets of choices
 *  - which columns show, and how tall the rows are - and an unlabelled run of
 *  ticked items reads as one list where the last two entries look like odd
 *  columns. Paired with role="group" + aria-label so it is a real grouping to a
 *  screen reader and not just a bold line. */
export const MenuGroupLabel = styled.p`
  margin: 0;
  padding: ${spacing.half} ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.wide};
  text-transform: uppercase;
  color: ${palette.slate};
`;

export const MenuItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  width: 100%;
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.xs};
  text-align: left;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.ink};
  background: none;
  border: none;
  border-radius: ${radius.xs};
  cursor: pointer;

  &:hover {
    background: ${palette.irisTintSoft};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const MenuAnchor = styled.div`
  position: relative;
  display: inline-flex;
`;

export const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  background: ${palette.warningSoft};
  border: 1px solid ${palette.warning};
  border-radius: ${radius.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.inkSoft};
`;
